import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControlLabel,
  Checkbox,
  Divider,
} from '@mui/material';
import React, { useCallback, useState, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useBreakpoints, useItemAttributes } from 'hooks';
import { IItemKey } from 'api/catalog/models';
import { getTypedKeys } from 'utils/getTypedKeys';
import { getLabelClasses } from 'ui/Refine/FacetSection';

import messages from '../messages';
import { IVisibilityOptionsDialogProps } from '..';

interface IProps extends IVisibilityOptionsDialogProps {
  onToggleDialogOpen?: () => void;
}

const VisibilityOptionsDialog = ({
  disabledOptions = [],
  defaultOptions = [],
  selectedOptions = [],
  onSelectedOptionsChange = () => null,
  onToggleDialogOpen = () => null,
}: IProps) => {
  const { isMobile } = useBreakpoints();
  const { formatMessage } = useIntl();
  const { getLabel, attributes } = useItemAttributes();

  const getInitialValues = useCallback(() => {
    const ret: Partial<Record<IItemKey, boolean>> = {};
    attributes.forEach((attr) => {
      ret[attr] =
        selectedOptions !== undefined && selectedOptions.includes(attr);
    });
    return ret;
  }, [attributes, selectedOptions]);

  const [itemFieldStatus, setItemFieldStatus] = useState<
    Partial<Record<IItemKey, boolean>>
  >(getInitialValues());

  const selectedColumns = useMemo(
    () =>
      getTypedKeys(itemFieldStatus).filter(
        (key: IItemKey) => itemFieldStatus[key] === true
      ),
    [itemFieldStatus]
  );

  const handleReset = useCallback(() => {
    setItemFieldStatus((prev) => {
      const copy = { ...prev };
      getTypedKeys(copy).forEach((key) => {
        copy[key] = defaultOptions.includes(key);
      });
      return copy;
    });
  }, [defaultOptions]);

  const handleApply = useCallback(() => {
    onSelectedOptionsChange && onSelectedOptionsChange(selectedColumns);
    onToggleDialogOpen();
  }, [onSelectedOptionsChange, selectedColumns, onToggleDialogOpen]);

  const handleChangeSelection = useCallback((key: IItemKey) => {
    setItemFieldStatus((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  const fixedSelected = useMemo(
    () => disabledOptions.filter((col) => itemFieldStatus[col]),
    [disabledOptions, itemFieldStatus]
  );

  const fixedUnselected = useMemo(
    () => disabledOptions.filter((col) => !itemFieldStatus[col]),
    [disabledOptions, itemFieldStatus]
  );

  const isSomethingSelectableSelected = useMemo(
    () => selectedColumns.length > fixedSelected.length,
    [fixedSelected.length, selectedColumns.length]
  );

  const isEverythingSelectableSelected = useMemo(
    () => selectedColumns.length === attributes.length - fixedUnselected.length,
    [attributes.length, fixedUnselected.length, selectedColumns.length]
  );

  const isIndeterminate = useMemo(
    () => isSomethingSelectableSelected && !isEverythingSelectableSelected,
    [isEverythingSelectableSelected, isSomethingSelectableSelected]
  );

  const handleSelectAllChange = useCallback(() => {
    const newVal: Partial<Record<IItemKey, boolean>> = {};
    const isSettingToTrue =
      isIndeterminate || selectedColumns.length === fixedSelected.length;
    attributes
      .filter((attr) => !disabledOptions.includes(attr))
      .forEach((attr) => {
        newVal[attr] = isSettingToTrue;
      });
    setItemFieldStatus((prev) => ({ ...prev, ...newVal }));
  }, [
    attributes,
    disabledOptions,
    fixedSelected.length,
    isIndeterminate,
    selectedColumns.length,
  ]);

  return (
    <Dialog fullScreen={isMobile} open onClose={onToggleDialogOpen}>
      <DialogTitle>
        <FormattedMessage {...messages.tableOptions} />
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <FormattedMessage {...messages.visibleFields} />
        </DialogContentText>
        <Divider />
        <Box
          padding='10px'
          display='flex'
          justifyContent='center'
          alignItems='center'
        >
          <Button variant='outlined' onClick={handleReset}>
            <FormattedMessage {...messages.resetButton} />
          </Button>
        </Box>

        <Divider />
        <FormControlLabel
          sx={getLabelClasses(true)}
          control={
            <Checkbox
              size='small'
              checked={isEverythingSelectableSelected}
              onChange={handleSelectAllChange}
              indeterminate={isIndeterminate}
            />
          }
          label={formatMessage(messages.selectAll)}
        />
        {attributes.map((attr) => (
          <FormControlLabel
            sx={getLabelClasses(false)}
            key={attr}
            control={
              <Checkbox
                size='small'
                checked={itemFieldStatus[attr] || false}
                disabled={disabledOptions.includes(attr)}
                onChange={() => handleChangeSelection(attr)}
              />
            }
            label={getLabel(attr)}
          />
        ))}
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={onToggleDialogOpen}>
          <FormattedMessage {...messages.cancelButton} />
        </Button>
        <Button variant='contained' onClick={handleApply}>
          <FormattedMessage {...messages.applyButton} />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VisibilityOptionsDialog;
